import {
  AddDefectsToSplitParams,
  AddOperatorToShiftParams,
  AddOperatorToSplitParams,
  AddRawMaterialToShiftParams,
  AddSkuTargetToWorkOrderParams,
  AddSkuToShiftParams,
  AddSkuToSplitParams,
  BasicProductionAdvancementType,
  CreateBasicProductionAdvancementParams,
  CreateFullProductionAdvancementBulkParams,
  CreateOperatorResponse,
  CreateProductionDataBulkParams,
  CreateProductionDataParams,
  CreateWorkOrderForFullProduction,
  DeleteProductionAdvancementParams,
  DeleteProductionDataParams,
  DeleteSkuFromWorkOrderParams,
  GetBasicProductionDataParams,
  GetProductionDataParams,
  GetWorkingMinutesParams,
  IProductionService,
  ProductionItemType,
  QuantitySuggestedByMachineResponse,
  ReviewProductionAdvancementParams,
  UpdateBasicProductionAdvancementParams,
  UpdateProductionDataParams,
  UpdateWorkOrderParams,
  UpdateWorkOrderSkuTargetParams,
  WorkOrderDataTargets
} from './contracts/production.service';

import { ProductionConfig } from '_clean-code/ducks/production/reducers';
import apiClient from '../../api/index';
import { createUrl } from '_clean-code/utils/urlHelper';
/* eslint-disable max-lines */
import momentTz from 'moment-timezone';
import { validate as isValidUUID } from 'uuid';

const ProductionService: IProductionService = {
  getAllProduction: async (
    params: GetProductionDataParams
  ): Promise<ProductionItemType[]> => {
    const response = await apiClient.request(
      'GET',
      createUrl('/work-steps')
        .addQueryParamMany('machineId', params.machineIds)
        .addQueryParam('status', params.status)
        .addQueryParam(
          'start',
          params.start &&
            momentTz(params.start).startOf('day').toDate().toISOString()
        )
        .addQueryParam(
          'end',
          params.end &&
            momentTz(params.end)
              .startOf('day')
              .add(1, 'day')
              .toDate()
              .toISOString()
        )
        .addQueryParam('onlyCurrentShifts', params.onlyCurrentShifts)
        .toStringUrl()
    );

    return response as ProductionItemType[];
  },
  getBasicProductionAdvancement: async (
    _params: GetBasicProductionDataParams
  ): Promise<BasicProductionAdvancementType[]> => {
    const params: any = { ..._params };
    delete params.rowDataType;
    if ((_params.operatorId?.length ?? 0) === 0) {
      delete params.operatorId;
    }
    if (_params.simpleWorkOrderId.length > 0) {
      params.simpleWorkOrderId = _params.simpleWorkOrderId.map(
        (item) => item.value
      );
    } else {
      delete params.simpleWorkOrderId;
    }
    if (_params.workOrderId.length > 0) {
      params.workOrderId = _params.workOrderId.map((item) => item.value);
    } else {
      delete params.workOrderId;
    }
    if ((_params.shifts?.length ?? 0) === 0) {
      delete params.shifts;
    }
    if (_params.start) {
      params.start = momentTz(_params.start).toISOString();
    }
    if (_params.end) {
      params.end = momentTz(_params.end).toISOString();
    }
    const response = await apiClient.request(
      'POST',
      createUrl('/production-advancements/filters').toStringUrl(),
      params
    );

    return response as BasicProductionAdvancementType[];
  },
  createProduction: async (
    params: CreateProductionDataParams
  ): Promise<ProductionItemType> => {
    const response = await apiClient.request(
      'POST',
      createUrl('/work-steps').toStringUrl(),
      params
    );

    return response as ProductionItemType;
  },

  updateProduction: async (
    params: UpdateProductionDataParams
  ): Promise<ProductionItemType> => {
    const { id, ...newParams } = params;
    const response = await apiClient.request(
      'PUT',
      createUrl(`/work-steps/${id}`).toStringUrl(),
      newParams
    );

    return response as ProductionItemType;
  },

  deleteProduction: async (
    params: DeleteProductionDataParams
  ): Promise<void> => {
    await apiClient.request(
      'DELETE',
      createUrl(`/work-steps/${params.workId}`).toStringUrl(),
      params
    );
  },

  getWorkingMinutes: async (
    params: GetWorkingMinutesParams
  ): Promise<number> => {
    const { machineId, start, end } = params;
    if (isNaN(machineId) || machineId === 0) return 0;
    if (start.getTime() >= end.getTime()) return 0;
    const response = await apiClient.request(
      'GET',
      createUrl(
        `/work-steps/working-minutes/${machineId}?start=${start.toISOString()}&end=${end.toISOString()}`
      ).toStringUrl()
    );
    return response as number;
  },
  createProductionBulk: async (
    params: CreateProductionDataBulkParams[]
  ): Promise<ProductionItemType[]> => {
    const response = await apiClient.request(
      'POST',
      createUrl('/work-steps/bulk').toStringUrl(),
      params
    );

    return response as ProductionItemType[];
  },
  reviewProductionAdvancement: async (
    params: ReviewProductionAdvancementParams
  ): Promise<void> => {
    return apiClient.request(
      'PATCH',
      createUrl(`/production-advancements`).toStringUrl(),
      params
    );
  },
  deleteProductionAdvancement: async (
    params: DeleteProductionAdvancementParams
  ): Promise<void> => {
    await apiClient.request(
      'DELETE',
      createUrl(`/production-advancements/${params.id}`).toStringUrl()
    );
  },
  createBasicProductionAdvancement: async (
    params: CreateBasicProductionAdvancementParams
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl('/production-advancements').toStringUrl(),
      params
    );
  },
  updateBasicProductionAdvancement: async (
    params: UpdateBasicProductionAdvancementParams
  ): Promise<void> => {
    await apiClient.request(
      'PUT',
      createUrl(`/production-advancements/${params.id}`).toStringUrl(),
      params
    );
  },
  splitBasicProductionAdvancement: async (
    id: string,
    params: CreateBasicProductionAdvancementParams[]
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl(`/production-advancements/${id}/split`).toStringUrl(),
      params
    );
  },
  getQuantitySuggestedByMachine: async (
    machineId: string,
    dataRange: {
      start: string;
      end: string;
    }[]
  ): Promise<QuantitySuggestedByMachineResponse[]> => {
    const response = await apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${machineId}/quantity/suggestions`
      ).toStringUrl(),
      dataRange
    );
    return response as QuantitySuggestedByMachineResponse[];
  },
  createBasicProductionAdvancementBulk: async (
    params: CreateBasicProductionAdvancementParams[]
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl('/production-advancements/basic/bulk').toStringUrl(),
      params
    );
  },
  createFullProductionAdvancementBulk: async (
    params: CreateFullProductionAdvancementBulkParams[]
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl('/production-advancements/bulk').toStringUrl(),
      params
    );
  },
  createWorkOrderForFullProduction: async (
    params: CreateWorkOrderForFullProduction
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl('/work-orders/production-full').toStringUrl(),
      params
    );
  },
  getWorkOrderTargetDetails: async (
    workOrderId: string
  ): Promise<WorkOrderDataTargets> => {
    const response = await apiClient.request(
      'GET',
      createUrl(`/work-order-target/${workOrderId}`).toStringUrl()
    );
    return response as WorkOrderDataTargets;
  },
  updateWorkOrderSkuTarget: async (
    params: UpdateWorkOrderSkuTargetParams
  ): Promise<void> => {
    await apiClient.request(
      'PATCH',
      createUrl(
        `/work-order-target/${params.workOrderId}/${params.skuId}`
      ).toStringUrl(),
      {
        targetQuantity: params.targetQuantity
      }
    );
  },
  deleteSkuFromWorkOrder: async (
    params: DeleteSkuFromWorkOrderParams
  ): Promise<void> => {
    await apiClient.request(
      'DELETE',
      createUrl(
        `/work-order-target/${params.workOrderId}/${params.skuId}`
      ).toStringUrl()
    );
  },
  addSkuTargetToWorkOrder: async (
    params: AddSkuTargetToWorkOrderParams
  ): Promise<void> => {
    await apiClient.request(
      'POST',
      createUrl(`/work-order-target/${params.workOrderId}`).toStringUrl(),
      {
        skuId: params.skuId,
        targetQuantity: params.targetQuantity
      }
    );
  },
  updateWorkOrder: async (_params: UpdateWorkOrderParams): Promise<void> => {
    const params: Partial<UpdateWorkOrderParams> = { ..._params };
    delete params.id;
    await apiClient.request(
      'PATCH',
      createUrl(`/work-orders/${_params.id}/production-full`).toStringUrl(),
      {
        ...params,
        workOrder: {
          ...params.workOrder,
          priority: Number(params.workOrder!.priority)
        }
      }
    );
  },
  addSkuToShift: async (_params: AddSkuToShiftParams): Promise<void> => {
    const params: Partial<AddSkuToShiftParams> = { ..._params };
    delete params.shiftId; //excess property
    delete params.isProdFull; //excess property

    if (_params.isProdFull) {
      await apiClient.request(
        'POST',
        createUrl(
          `/production-advancements/${_params.shiftId}/entity/production-full`
        ).toStringUrl(),
        {
          ...params,
          skuId: params.skuId,
          start: params.start,
          nominalSpeed: params.nominalSpeed,
          workOrderId: params.workOrderId,
          sku: params.sku
        }
      );
    } else {
      await apiClient.request(
        'POST',
        createUrl(
          `/production-advancements/${_params.shiftId}/entity`
        ).toStringUrl(),
        {
          ...params,
          skuId: params.skuId,
          start: params.start,
          nominalSpeed: params.nominalSpeed,
          sku: params.sku
        }
      );
    }
  },
  getSimpleWorkOrders: async (): Promise<any[]> => {
    const response = await apiClient.request(
      'GET',
      createUrl(`/simple-work-orders`).toStringUrl(),
      { page: 0, pageSize: 1000 }
    );
    return response.data as any[];
  },
  getSuggestedStartByMachine: async (
    machineId: number,
    type: string
  ): Promise<{ start: string; end: string }> => {
    const data = await apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${machineId}/timeframe/suggestions`
      ).toStringUrl(),
      type,
      { skipSerialize: true }
    );
    return data;
  },
  getSettingsForProduction: async (): Promise<{
    workOrderForProductionAdvancement: boolean;
    showDefectsCatalogue: boolean;
    showOperatorsInProductionAdvancements: boolean;
  }> => {
    const response = await apiClient.request(
      'GET',
      createUrl('/configuration').toStringUrl()
    );
    return response as {
      workOrderForProductionAdvancement: boolean;
      showDefectsCatalogue: boolean;
      showOperatorsInProductionAdvancements: boolean;
    };
  },
  updateSettingsForProduction: async (
    data: ProductionConfig
  ): Promise<void> => {
    const _data = data;
    if (_data.stopDaysInTablet === null) {
      //TODO: remove this line when the backend is fixed
      delete _data.stopDaysInTablet;
    }
    await apiClient.request(
      'PATCH',
      createUrl('/configuration').toStringUrl(),
      _data
    );
  },
  addOperatorToShift: async (
    _params: AddOperatorToShiftParams
  ): Promise<void> => {
    const params: Partial<AddOperatorToShiftParams> = { ..._params };
    delete params.shiftId; //excess property
    delete params.isProdFull; //excess property
    await apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${_params.shiftId}/operator${
          _params.isProdFull ? '/production-full' : ''
        }`
      ).toStringUrl(),
      {
        ...params
      }
    );
  },
  addRawMaterialToShift: async (
    _params: AddRawMaterialToShiftParams
  ): Promise<void> => {
    const params: Partial<AddOperatorToShiftParams> = { ..._params };
    delete params.shiftId; //excess property
    delete params.isProdFull; //excess property
    await apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${_params.shiftId}/raw_material${
          _params.isProdFull ? '/production-full' : ''
        }`
      ).toStringUrl(),
      {
        ...params
      }
    );
  },
  async createOpearator(params): Promise<CreateOperatorResponse> {
    const [response] = await apiClient.request(
      'POST',
      '/identities/operators',
      [params]
    );
    return response;
  },
  async getPaginatedNominalSpeeds(params) {
    return apiClient.request(
      'POST',
      createUrl('/speed/filters').toStringUrl(),
      params
    );
  },
  async addOperatorToSplit(params: AddOperatorToSplitParams) {
    const _params = { ...params } as Partial<AddOperatorToSplitParams>;
    delete _params.shiftId;
    return apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${params.shiftId}/operator`
      ).toStringUrl(),
      _params
    );
  },
  async addDefectsToSplit(params: AddDefectsToSplitParams) {
    const productionOrderTag = isValidUUID(params.productionOrderTag)
      ? params.productionOrderTag ?? undefined
      : undefined;
    const _params = {
      simpleWorkOrderId: productionOrderTag,
      simpleWorkOrderName: productionOrderTag,
      custom: params.customFieldsValues,
      skuId: params.skuId,
      quantity: params.defectsCount,
      qualityIssueId: params.defectType ?? undefined
    };
    return apiClient.request(
      'POST',
      createUrl(
        `/production-advancements/${params.shiftId}/defects`
      ).toStringUrl(),
      _params
    );
  },
  async addSkuToSplit(params: AddSkuToSplitParams) {
    const _params = { ...params } as Partial<AddSkuToSplitParams>;
    delete _params.shiftId;
    return apiClient.request(
      'POST',
      createUrl(`/production-advancements/${params.shiftId}/sku`).toStringUrl(),
      _params
    );
  }
};

export default ProductionService;
